import * as htmlToImage from "html-to-image";

const MAX_CAPTURE_PIXELS = 2400000;

function timestamp() {
  return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}

const CAPTURE_FILTER_SKIP = new Set([
  "v-overlay",
  "v-tooltip",
  "sw-snackbar-stack",
  "user-menu-overlay",
  "sw-capture-dock",
  "sidebar-scrim",
  "org-settings-headless-host",
]);

export function getCaptureRoot() {
  return (
    document.querySelector(".landing-shell") ||
    document.querySelector(".app-content") ||
    document.querySelector(".app-main-root") ||
    document.querySelector("#app")
  );
}

function shouldIncludeCaptureNode(node) {
  if (!node || node.nodeType !== 1) return true;
  if (node.dataset?.captureSkip === "true") return false;

  const classList = node.classList;
  if (!classList) return true;

  for (const className of classList) {
    if (CAPTURE_FILTER_SKIP.has(className)) return false;
  }

  return true;
}

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function isTransparentColor(color) {
  if (!color) return true;
  const value = color.trim().toLowerCase();
  return (
    value === "transparent" ||
    value === "rgba(0, 0, 0, 0)" ||
    value === "rgba(0,0,0,0)"
  );
}

function getCaptureBackgroundColor(root) {
  const candidates = [
    root && getComputedStyle(root).backgroundColor,
    getComputedStyle(document.body).backgroundColor,
    getComputedStyle(document.documentElement).backgroundColor,
  ];

  for (const color of candidates) {
    if (!isTransparentColor(color)) return color;
  }

  const token = getComputedStyle(document.documentElement)
    .getPropertyValue("--sw-bg")
    .trim();

  return token || "#faf7fd";
}

function getFullCaptureSize(root) {
  const rect = root.getBoundingClientRect();
  const width = Math.max(
    1,
    Math.round(
      Math.max(
        root.scrollWidth || 0,
        root.offsetWidth || 0,
        root.clientWidth || 0,
        rect.width || 0,
      ),
    ),
  );
  const height = Math.max(
    1,
    Math.round(
      Math.max(
        root.scrollHeight || 0,
        root.offsetHeight || 0,
        root.clientHeight || 0,
        rect.height || 0,
      ),
    ),
  );

  return { width, height };
}

function getCapturePixelRatio(width, height) {
  const pixels = width * height;
  if (pixels <= MAX_CAPTURE_PIXELS) {
    return Math.min(2, window.devicePixelRatio || 1);
  }

  return Math.max(0.7, Math.sqrt(MAX_CAPTURE_PIXELS / pixels));
}

function withCaptureMode() {
  document.documentElement.classList.add("sw-capturing");

  return () => {
    document.documentElement.classList.remove("sw-capturing");
  };
}

export function triggerOsScreenshotShortcut() {
  if (typeof document === "undefined") return;

  const isMac = /Mac|iPhone|iPad|iPod/i.test(
    navigator.userAgent || navigator.platform || "",
  );

  // Windows: open Screen Clip (same crop UI as Win+Shift+S).
  // Synthetic keyboard events cannot reach the OS, so use the protocol handler.
  if (!isMac) {
    try {
      const anchor = document.createElement("a");
      anchor.href = "ms-screenclip:";
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return;
    } catch {
      /* fall through to key dispatch */
    }
  }

  const targets = [document.activeElement, document.body, document, window].filter(
    Boolean,
  );

  const dispatchKey = (type, init) => {
    for (const target of targets) {
      try {
        target.dispatchEvent(
          new KeyboardEvent(type, {
            ...init,
            bubbles: true,
            cancelable: true,
            composed: true,
          }),
        );
      } catch {
        /* ignore unsupported targets */
      }
    }
  };

  if (isMac) {
    const init = {
      key: "3",
      code: "Digit3",
      keyCode: 51,
      which: 51,
      metaKey: true,
      shiftKey: true,
    };
    dispatchKey("keydown", init);
    dispatchKey("keyup", init);
    return;
  }

  const init = {
    key: "s",
    code: "KeyS",
    keyCode: 83,
    which: 83,
    metaKey: true,
    shiftKey: true,
  };
  dispatchKey("keydown", init);
  dispatchKey("keyup", init);
}

export async function captureAppScreenshot() {
  const root = getCaptureRoot();
  if (!root) {
    throw new Error("Could not find the app area to capture.");
  }

  const restoreCaptureMode = withCaptureMode();

  let blob;
  try {
    // Let capture-only CSS expand overflow/contain so full UI is measurable.
    await waitForPaint();
    await waitForPaint();

    const { width, height } = getFullCaptureSize(root);
    const backgroundColor = getCaptureBackgroundColor(root);

    blob = await htmlToImage.toBlob(root, {
      width,
      height,
      pixelRatio: getCapturePixelRatio(width, height),
      skipFonts: true,
      cacheBust: false,
      quality: 0.92,
      backgroundColor,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: "none",
        overflow: "visible",
        backgroundColor,
      },
      filter: shouldIncludeCaptureNode,
    });
  } finally {
    restoreCaptureMode();
  }

  if (!blob) {
    throw new Error("Screenshot capture returned empty data.");
  }

  blob.name = `servicewrk-screenshot-${timestamp()}.png`;
  return blob;
}

export async function copyBlobToClipboard(blob) {
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    return false;
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob,
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || blob.name || "servicewrk-capture";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function persistCapture(blob, { preferClipboard = true } = {}) {
  let copied = false;

  if (preferClipboard) {
    copied = await copyBlobToClipboard(blob);
  }

  if (!copied) {
    downloadBlob(blob, blob.name);
  }

  return { copied, downloaded: !copied };
}

function getSupportedRecorderMimeType() {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "video/webm";
}

export class ScreenRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.stream = null;
    this.chunks = [];
    this._stopResolver = null;
  }

  get isRecording() {
    return this.mediaRecorder?.state === "recording";
  }

  async start() {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error("Screen recording is not supported in this browser.");
    }

    this.stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });

    this.chunks = [];
    const mimeType = getSupportedRecorderMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data?.size) this.chunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.chunks, {
        type: this.mediaRecorder?.mimeType || mimeType,
      });
      blob.name = `servicewrk-recording-${timestamp()}.webm`;
      this.cleanup();
      this._stopResolver?.(blob);
      this._stopResolver = null;
    };

    const videoTrack = this.stream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.onended = () => {
        if (this.isRecording) this.stop();
      };
    }

    this.mediaRecorder.start(1000);
  }

  stop() {
    if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      this._stopResolver = resolve;
      this.mediaRecorder.stop();
    });
  }

  cleanup() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.mediaRecorder = null;
    this.chunks = [];
  }
}
